import { useQuery } from '@tanstack/react-query';

import { qk } from '@core/api/queryKeys';
import type { LatestPositionsResponse } from '@core/types/api';
import { positionsService } from '@/features/fleet/api/positionsService';

import { useLiveTracking } from './LiveTrackingProvider';

/**
 * Live positions, however they happen to be arriving.
 *
 * The elegance here is that the polling fallback IS the snapshot query -- SSE just
 * switches its interval off. One cache entry, two transports, so the map and any list
 * reading the same key can never disagree about where a vehicle is. The alternative,
 * a separate "live" store merged with a "snapshot" store, is where that divergence
 * comes from.
 */
export function useLivePositions() {
  const { status } = useLiveTracking();
  const streaming = status === 'live' || status === 'paused';

  return useQuery<LatestPositionsResponse>({
    queryKey: qk.positions.latest(),
    queryFn: ({ signal }) => positionsService.fetchLatest(signal),
    staleTime: 30_000,
    // Paused counts as streaming: the tab is hidden, so polling would burn battery for
    // a screen nobody is looking at. `refetchOnWindowFocus` catches up on return.
    refetchInterval: streaming ? false : 60_000,
    refetchIntervalInBackground: false,
  });
}
